import type { Grid as ContourGridType, Cell } from './contourTracer';
import type { Grid as HoneycombGrid, Hex } from 'honeycomb-grid';

interface Axial {
  q: number; r: number;
}

/**
 * Implementation of the Grid interface for a standard square grid.
 */
export const squareGrid: ContourGridType = {
  numDirs: 4,
  getNeighbor(cell: Cell, dir: number): Cell {
    const dr = [-1, 0, 1, 0];
    const dc = [0, 1, 0, -1];
    return { row: cell.row + dr[dir]!, col: cell.col + dc[dir]! };
  },
  getVertex(cell: Cell, dir: number): [number, number] {
    switch (dir) {
      case 0: return [cell.col, cell.row]; // N
      case 1: return [cell.col + 1, cell.row]; // E
      case 2: return [cell.col + 1, cell.row + 1]; // S
      case 3: return [cell.col, cell.row + 1]; // W
      default: return [cell.col, cell.row];
    }
  },
  getTurn: (dir, amount) => (dir + amount + 4) % 4,
};

// Directions for pointy-top hexes: 0:E, 1:NE, 2:NW, 3:W, 4:SW, 5:SE
const pointyHexAxialDirections: [number, number][] = [
  [+1, 0], [+1, -1], [0, -1],
  [-1, 0], [-1, +1], [0, +1]
];

/**
 * Implementation of the Grid interface for a "pointy-top" hex grid.
 */
export const createPointyHexGrid = (grid: HoneycombGrid<Hex>): ContourGridType<[number, number]> => {
  return {
    numDirs: 6,
    getNeighbor(cell: Cell, dir: number): Cell {
      const axial = offsetToAxial(cell);
      const [dq, dr] = pointyHexAxialDirections[dir]!;
      const neighborAxial = { q: axial.q + dq, r: axial.r + dr };
      return axialToOffset(neighborAxial);
    },
    getVertex(cell: Cell, dir: number): [number, number] | undefined {
      if (!grid || grid.length === 0) return undefined;

      const axialCoords = offsetToAxial(cell);
      const hex = grid.get({ x: axialCoords.q, y: axialCoords.r });
      if (!hex) return undefined;

      const corners = hex.corners;
      const cornerMap = [0, 5, 4, 3, 2, 1];
      const cornerIndex = cornerMap[dir]!;
      const corner = corners[cornerIndex]!;
      return [corner.x, corner.y];
    },
    getTurn: (dir, amount) => (dir + amount + 6) % 6,
  };
};

export function formatLogObject(obj: Record<string, unknown>): Record<string, unknown> {
  if (!obj) return obj;
  const formatted: Record<string, unknown> = {};
  for (const key in obj) {
    if (typeof obj[key] === 'number') {
      formatted[key] = parseFloat(obj[key].toFixed(2));
    } else if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
      formatted[key] = formatLogObject(obj[key] as Record<string, unknown>);
    } else {
      formatted[key] = obj[key];
    }
  }
  return formatted;
};

export function offsetToAxial(cell: Cell): Axial {
  const q = cell.col;
  const r = cell.row - (cell.col - (cell.col & 1)) / 2; // odd-q
  return { q, r };
}

export function axialToOffset(axial: Axial): Cell {
  const col = axial.q;
  const row = axial.r + (axial.q - (axial.q & 1)) / 2; // odd-q
  return { row, col };
}